import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { LogoMark } from '@/components/brand/Logo';
import { BrandButton } from '@/components/brand/Buttons';
import { useGoogleLogin } from '@react-oauth/google';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ id, label, hint, children }) => (
  <label htmlFor={id} className="block">
    <span className="block text-[13px] font-medium text-ink-soft mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-xs text-ink-muted mt-1.5">{hint}</span>}
  </label>
);

const baseInput =
  'w-full h-11 px-4 rounded-xl bg-cream-50 border border-border placeholder:text-ink-muted/70 text-ink ' +
  'focus:outline-none focus:ring-2 focus:ring-clay-500/40 focus:border-clay-300 transition';

const AuthModalSimplified = ({ isOpen, onClose, mode = 'signup', onSwitchMode }) => {
  const [isLogin, setIsLogin] = useState(mode === 'login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ name: '', email: '', password: '' });

  // Forgot-password sub-flow: null (normal auth) | 'email' | 'code'
  const [resetStep, setResetStep] = useState(null);
  const [resetData, setResetData] = useState({ email: '', otp: '', newPassword: '' });

  const startReset = () => {
    setResetStep('email');
    setResetData({ email: formData.email || '', otp: '', newPassword: '' });
  };

  const backToLogin = () => {
    setResetStep(null);
    setIsLogin(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: resetData.email });
      toast.success('If that email has an account, a code is on its way.');
      setResetStep('code');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the reset code');
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (!resetData.otp.trim()) return toast.error('Enter the code from your email');
    if (resetData.newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetData.email,
        otp: resetData.otp.trim(),
        new_password: resetData.newPassword,
      });
      toast.success('Password updated — sign in with your new password.');
      setFormData((p) => ({ ...p, email: resetData.email, password: '' }));
      backToLogin();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset the password');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const googleLogin = useGoogleLogin({
    flow: 'implicit',
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        await loginWithGoogle({ access_token: tokenResponse.access_token });
        toast.success('Welcome.');
        onClose();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Couldn't sign in with Google");
      } finally {
        setLoading(false);
      }
    },
    onError: () => toast.error("Couldn't sign in with Google"),
  });

  const handleGoogleLogin = () => googleLogin();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!isLogin && !formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back.');
        onClose();
      } else {
        await signup({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: '',
          address: '',
          city: '',
          state: '',
          pincode: '',
          country: 'India',
        });
        toast.success('Your account is ready.');
        onClose();
        setTimeout(() => {
          toast.info("Let's begin your basket.");
          navigate('/registry/new');
        }, 1000);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Authentication failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    const next = !isLogin;
    setIsLogin(next);
    setFormData({ name: '', email: '', password: '' });
    if (onSwitchMode) onSwitchMode(next ? 'login' : 'signup');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-[440px] bg-cream-50 border-border/70 p-0 overflow-hidden rounded-3xl shadow-soft-3"
      >
        {resetStep ? (
          <div className="px-8 pt-9 pb-8">
            <LogoMark size={28} className="text-clay-700 mx-auto mb-5" />
            <h2 className="font-display text-[26px] leading-tight text-ink text-center">
              {resetStep === 'email' ? 'Reset your password.' : 'Check your email.'}
            </h2>
            <p className="mt-2 text-sm text-ink-soft text-center">
              {resetStep === 'email'
                ? "Enter your email and we'll send a 6-digit code."
                : `We sent a code to ${resetData.email}. It's valid for 15 minutes.`}
            </p>

            {resetStep === 'email' ? (
              <form onSubmit={submitForgot} className="mt-6 space-y-4">
                <Field id="reset-email" label="Email">
                  <Input
                    id="reset-email"
                    type="email"
                    required
                    autoFocus
                    value={resetData.email}
                    onChange={(e) => setResetData((p) => ({ ...p, email: e.target.value }))}
                    placeholder="you@example.com"
                    className={baseInput}
                  />
                </Field>
                <BrandButton type="submit" size="lg" disabled={loading} className="w-full h-12">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Send reset code
                </BrandButton>
              </form>
            ) : (
              <form onSubmit={submitReset} className="mt-6 space-y-4">
                <Field id="reset-otp" label="6-digit code">
                  <Input
                    id="reset-otp"
                    inputMode="numeric"
                    required
                    autoFocus
                    value={resetData.otp}
                    onChange={(e) => setResetData((p) => ({ ...p, otp: e.target.value }))}
                    placeholder="123456"
                    className={baseInput}
                  />
                </Field>
                <Field id="reset-password" label="New password" hint="At least 6 characters.">
                  <Input
                    id="reset-password"
                    type="password"
                    required
                    value={resetData.newPassword}
                    onChange={(e) => setResetData((p) => ({ ...p, newPassword: e.target.value }))}
                    placeholder="Create a new password"
                    className={baseInput}
                  />
                </Field>
                <BrandButton type="submit" size="lg" disabled={loading} className="w-full h-12">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Set new password
                </BrandButton>
              </form>
            )}

            <button
              type="button"
              onClick={backToLogin}
              className="mt-5 mx-auto flex items-center gap-1.5 text-sm text-ink-soft hover:text-clay-700"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
            </button>
          </div>
        ) : (
        <>
        {/* Header */}
        <div className="px-8 pt-9 pb-2 text-center">
          <LogoMark size={28} className="text-clay-700 mx-auto mb-5" />
          <h2 className="font-display text-[28px] leading-tight text-ink">
            {isLogin ? 'Welcome back.' : (
              <>
                Begin a basket
                <span
                  className="italic text-clay-700 ml-1.5"
                  style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}
                >
                  with us.
                </span>
              </>
            )}
          </h2>
          <p className="mt-2 text-sm text-ink-soft">
            {isLogin
              ? 'Sign in to your registries and gifts.'
              : 'Free, always — for you and every guest.'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="px-8 pb-8 pt-5 space-y-4">
          <BrandButton
            type="button"
            variant="outline"
            size="lg"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full h-12 border-border bg-cream-50 hover:bg-cream-100"
          >
            <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" aria-hidden>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Continue with Google</span>
          </BrandButton>

          <div className="relative my-1">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-cream-50 px-3 text-[11px] uppercase tracking-[0.18em] text-ink-muted">
                or with email
              </span>
            </div>
          </div>

          {!isLogin && (
            <Field id="name" label="Your name">
              <Input
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Aanya Sharma"
                className={baseInput}
              />
            </Field>
          )}

          <Field id="email" label="Email">
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              className={baseInput}
            />
          </Field>

          <Field
            id="password"
            label="Password"
            hint={!isLogin ? 'At least 6 characters. We never see it.' : undefined}
          >
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.password}
                onChange={handleChange}
                placeholder={isLogin ? 'Your password' : 'Create a password'}
                className={`${baseInput} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>

          {isLogin && (
            <div className="flex justify-end -mt-1">
              <button
                type="button"
                onClick={startReset}
                className="text-[13px] text-clay-700 hover:text-clay-800 underline decoration-clay-300 underline-offset-2"
              >
                Forgot password?
              </button>
            </div>
          )}

          <BrandButton
            type="submit"
            size="lg"
            disabled={loading}
            className="w-full h-12 mt-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {isLogin ? 'Sign in' : 'Create your account'}
          </BrandButton>

          <p className="text-center text-sm text-ink-soft pt-2">
            {isLogin ? "New here? " : 'Already with us? '}
            <button
              type="button"
              onClick={switchMode}
              className="text-clay-700 hover:text-clay-800 font-medium underline decoration-clay-300 underline-offset-4"
            >
              {isLogin ? 'Begin a basket' : 'Sign in'}
            </button>
          </p>
        </form>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModalSimplified;
